import {
  Alert,
  Button,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {useState} from 'react';

import {isEmpty, isNil} from 'ramda';

import {useGetSalesFinanceSettingsQuery, useSetSalesFinanceSettingsMutation} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, useIsLicenceEnabled, useTenant} from '@dms/shared';

import {ApiError} from 'shared/web';

import {
  PurchasePriceCostsFormSchema,
  PurchasePriceCostsFormSchemaType,
} from './schemas/PurchasePriceCostsFormSchema';

const toBigDecimalString = (value: number | null) => (isNil(value) ? null : value.toString());

const fromBigDecimalString = (value: string | null | undefined) =>
  isNil(value) ? null : Number(value);

interface PurchasePriceCostsFormProps {
  isReadOnly: boolean;
}

export function PurchasePriceCostsForm(props: PurchasePriceCostsFormProps) {
  const {isPriceReportEnabled} = useIsLicenceEnabled();
  const {tenantCurrency} = useTenant();
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const {
    data: salesFinanceSettings,
    isLoading: isSalesFinanceSettingsLoading,
    isError: isSalesFinanceSettingsError,
  } = useGetSalesFinanceSettingsQuery();

  const [setSalesFinanceSettings] = useSetSalesFinanceSettingsMutation();
  const [saveError, setSaveError] = useState<ReturnType<typeof getApiErrorMessages> | null>(null);

  const currencySymbol = formatCurrencySymbol(tenantCurrency);
  const currencyLabel = isEmpty(currencySymbol) ? tenantCurrency : currencySymbol;

  const handleSubmit: FormSubmitHandler<PurchasePriceCostsFormSchemaType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    // Without the Price Report licence the backend ignores the cost fields. Omitting them keeps
    // the stored values, whereas an explicit null would clear them.
    const costs = isPriceReportEnabled
      ? {
          dailyStorageCost: toBigDecimalString(values.dailyStorageCost),
          fixedPurchaseCosts: toBigDecimalString(values.fixedPurchaseCosts),
        }
      : {};

    await setSalesFinanceSettings({
      salesFinanceSettingsRequestBody: {
        defaultDailyInterestRate: toBigDecimalString(values.defaultDailyInterestRate),
        expectedDaysToSell: values.expectedDaysToSell,
        ...costs,
      },
    })
      .unwrap()
      .then(() => {
        setSaveError(null);
        showNotification.success(
          i18n.t('page.settings.purchasePrice.costParameters.savedNotification')
        );
        reset(values);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {
          silent: true,
          callback: ({error}) => setSaveError(getApiErrorMessages(error)),
          signalApiError: blockerContext?.reportApiFailure,
        })
      );
  };

  const defaultValues: PurchasePriceCostsFormSchemaType = {
    defaultDailyInterestRate: fromBigDecimalString(salesFinanceSettings?.defaultDailyInterestRate),
    expectedDaysToSell: salesFinanceSettings?.expectedDaysToSell ?? null,
    dailyStorageCost: fromBigDecimalString(salesFinanceSettings?.dailyStorageCost),
    fixedPurchaseCosts: fromBigDecimalString(salesFinanceSettings?.fixedPurchaseCosts),
  };

  return (
    <DataStatus
      isLoading={isSalesFinanceSettingsLoading}
      isError={isSalesFinanceSettingsError}
      minHeight={60}
    >
      <Form<PurchasePriceCostsFormSchemaType>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isDisabled={props.isReadOnly}
        shouldWatchForUnsavedChanges
        experimentalZodSchema={PurchasePriceCostsFormSchema}
      >
        {(control, formApi) => (
          <Card
            title={i18n.t('page.settings.purchasePrice.costParameters.title')}
            flags={
              formApi.formState.isDirty && !props.isReadOnly
                ? [
                    {
                      label: i18n.t('general.notifications.unsavedChanges.title'),
                      colorScheme: 'orange',
                      isSubtle: true,
                    },
                  ]
                : undefined
            }
            data-testid={testIds.settings.purchasePrice('costParameters')}
          >
            <VStack spacing={4}>
              <Text size="small" color="secondary">
                {i18n.t('page.settings.purchasePrice.costParameters.description')}
              </Text>

              <Grid spacing={4} columns={2}>
                <FormField
                  name="defaultDailyInterestRate"
                  type="number"
                  decimalPlaces={2}
                  label={i18n.t('page.settings.purchasePrice.costParameters.annualFinancingRate')}
                  helperText={i18n.t(
                    'page.settings.purchasePrice.costParameters.annualFinancingRateHelper'
                  )}
                  suffix="%"
                  control={control}
                  data-testid={testIds.settings.purchasePrice('annualFinancingRate')}
                />

                <FormField
                  name="expectedDaysToSell"
                  type="number"
                  label={i18n.t('page.settings.purchasePrice.costParameters.expectedDaysToSell')}
                  helperText={i18n.t(
                    'page.settings.purchasePrice.costParameters.expectedDaysToSellHelper'
                  )}
                  suffix={i18n.t('entity.financing.labels.days')}
                  control={control}
                  data-testid={testIds.settings.purchasePrice('expectedDaysToSell')}
                />

                <Show when={isPriceReportEnabled}>
                  <FormField
                    name="dailyStorageCost"
                    type="number"
                    decimalPlaces={2}
                    label={i18n.t('page.settings.purchasePrice.costParameters.dailyStorageCost')}
                    helperText={i18n.t(
                      'page.settings.purchasePrice.costParameters.dailyStorageCostHelper'
                    )}
                    suffix={i18n.t('page.settings.purchasePrice.costParameters.perDaySuffix', {
                      currency: currencyLabel,
                    })}
                    control={control}
                    data-testid={testIds.settings.purchasePrice('dailyStorageCost')}
                  />

                  <FormField
                    name="fixedPurchaseCosts"
                    type="currency"
                    currency={tenantCurrency}
                    label={i18n.t('page.settings.purchasePrice.costParameters.fixedPurchaseCosts')}
                    helperText={i18n.t(
                      'page.settings.purchasePrice.costParameters.fixedPurchaseCostsHelper'
                    )}
                    control={control}
                    data-testid={testIds.settings.purchasePrice('fixedPurchaseCosts')}
                  />
                </Show>
              </Grid>

              <Show when={saveError}>
                {(saveError) => (
                  <Alert
                    type="inline"
                    variant="error"
                    message={saveError}
                    data-testid={testIds.settings.purchasePrice('saveError')}
                  />
                )}
              </Show>

              <Show when={!props.isReadOnly}>
                <HStack justify="flex-end" spacing={2}>
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.discardChanges')}
                    onClick={() => {
                      formApi.reset();
                      setSaveError(null);
                    }}
                    data-testid={testIds.settings.purchasePrice('discard')}
                  />
                  <FormButton
                    type="submit"
                    control={control}
                    title={i18n.t('general.actions.save')}
                    data-testid={testIds.settings.purchasePrice('save')}
                  />
                </HStack>
              </Show>
            </VStack>
          </Card>
        )}
      </Form>
    </DataStatus>
  );
}
